/**
 * Vallée du lac (GDD section 10, the map of the prototype): sand dunes to the west, a lake in the middle, a
 * glacier to the east, a floating island of rock over the lake, trees, and a pocket of oil buried under the
 * dunes. Dangers: drowning, unstable sand. Key: control the lake (freeze it to cross, or throw the enemy in).
 */
import { Physique, nextInt, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, rootedTree, sea, settle, tree, waves } from './common';

const { EMPTY, ROCK, SAND, WATER, ICE, OIL } = Physique;

export function generateValley(g: Gen): MapState {
  const { w, rng, seaY, data } = g;
  const at = (pct: number, jitter: number) => Math.trunc((w * pct) / 100) + nextInt(rng, 2 * jitter + 1) - jitter;
  const coastL = at(11, 8);
  const coastR = at(89, 8);
  const lakeL = at(40, 20);
  const lakeR = lakeL + at(22, 15);
  const relief = waves(g, 18, 4, 9, 11, 3, 29);
  const west = (x: number) => 82 + relief(x);
  const east = (x: number) => 96 + Math.trunc((Math.min(Math.max(0, x - lakeR), 70) * 42) / 70) + (relief(x) >> 1);
  const rimL = west(lakeL);
  const rimR = east(lakeR);
  const depth = 70 + nextInt(rng, 15);
  const elevation = (x: number) => {
    if (x <= lakeL) return west(x);
    if (x >= lakeR) return east(x);
    const t = x - lakeL;
    const span = lakeR - lakeL;
    return rimL + Math.trunc(((rimR - rimL) * t) / span) - Math.trunc((depth * sinDeg(Math.trunc((180 * t) / span))) / TRIG_ONE);
  };
  const surface = island(g, coastL, coastR, 90, elevation);
  const rock = surface.slice();

  // The buried oil pocket, under the dunes, well below the surface.
  const oilX = coastL + 90 + nextInt(rng, Math.max(1, lakeL - coastL - 200));
  ellipse(g, oilX, surface[oilX] + 34 + nextInt(rng, 10), 34 + nextInt(rng, 12), 8 + nextInt(rng, 4), OIL, ROCK);

  // The lake: water up to some twenty cells under the lower rim.
  const lakeTop = seaY - Math.min(rimL, rimR) + 22;
  for (let x = lakeL; x <= lakeR; x++) for (let y = lakeTop; y < surface[x]; y++) if (data[y * w + x] === EMPTY) data[y * w + x] = WATER;

  // Dunes to the west, sand beaches at both coasts.
  const p = nextInt(rng, 360);
  layer(g, surface, coastL - 50, lakeL - 6, (x) => {
    const dune = sinDeg(Math.trunc((x * 360 * 9) / w) + p);
    return 4 + (dune > 0 ? Math.trunc((16 * dune) / TRIG_ONE) : 0);
  }, SAND);
  layer(g, surface, coastR - 40, coastR + 50, (x) => Math.max(0, 6 - Math.trunc(Math.abs(x - coastR) / 9)), SAND);
  layer(g, surface, coastL - 60, coastL - 51, () => 3, SAND);

  // The glacier: a thick layer of ice on the eastern plateau, split by a crevasse or two.
  const glacierL = lakeR + 30;
  const glacierR = coastR - 46;
  const bed = surface.slice();
  layer(g, surface, glacierL, glacierR, (x) => Math.min(x - glacierL, glacierR - x, 22) + (relief(x * 3) >> 2), ICE);
  for (let k = 0, n = 1 + nextInt(rng, 2); k < n; k++) {
    const cx = glacierL + 40 + nextInt(rng, Math.max(1, glacierR - glacierL - 80));
    for (let x = cx - 2; x <= cx + 2; x++) for (let y = surface[x]; y < bed[x]; y++) if (data[y * w + x] === ICE) data[y * w + x] = EMPTY;
  }

  // Trees on the western hills, between the dunes, and on the lake's banks.
  for (let x = coastL + 30; x < glacierL - 10; x += 26 + nextInt(rng, 40)) {
    if (x > lakeL - 10 && x < lakeR + 10 && surface[x] >= lakeTop - 2) continue; // not in the water
    if (Math.abs(surface[x - 8] - surface[x + 8]) > 10) continue; // not on a steep slope
    rootedTree(g, x, rock[x], surface[x], 24 + nextInt(rng, 18));
  }

  // The floating island over the lake: a flat top of sand on rock, a point underneath, a tree.
  const fx = Math.trunc((lakeL + lakeR) / 2) + nextInt(rng, 61) - 30;
  const fy = seaY - 210 - nextInt(rng, 30);
  const rx = 44 + nextInt(rng, 10);
  for (let x = fx - rx; x <= fx + rx; x++) {
    const d = Math.abs(x - fx);
    const top = fy - 3 + Math.trunc((d * d) / (rx * 12));
    const bottom = fy + Math.trunc(((rx - d) * 28) / rx);
    rect(g, x, top, x, bottom, ROCK);
    rect(g, x, top - 3, x, top - 1, d < rx - 4 ? SAND : EMPTY);
  }
  tree(g, fx + nextInt(rng, 21) - 10, fy - 6, 18);

  sea(g);
  settle(g);
  return plainMap('valley');
}
